import React, { useState } from 'react';
import CustomTextField from '../CustomTextField/CustomTextField.js';
import { MenuItem, Button, Box, CircularProgress } from '@mui/material';
import './JobApplicationForm.scss';
import GenericDialog from '../GenericDialog/GenericDialog.js';


const BASE_INITIAL_DATA = {
    position: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    resume: null,
    coverLetter: null,
    gender: '',
    veteranStatus: '',
    isHispanic: '',
    disabilityStatus: '',
};

const INITIAL_ERRORS = {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    resume: '',
};

const JobApplicationForm = (props) => {
    const { jobSpecificFields, jobTitle } = props;
    const [dialogState, setDialogState] = useState({
        open: false,
        title: '',
        message: '',
        severity: 'success'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState(INITIAL_ERRORS);
    const [showDetailsForEmployee, setShowDetailsForEmployee] = useState(false);
    const [showDetailsForDisableEmployee, setShowDetailsForDisableEmployee] = useState(false);
    const getInitialData = () => {
        const jobSpecificData = {};
        jobSpecificFields.forEach(field => {
            jobSpecificData[field.name] = '';
        });
        return { ...BASE_INITIAL_DATA, ...jobSpecificData, position: jobTitle };
    };

    const [formData, setFormData] = useState(getInitialData());
    const toggleDetailsForEmployee = () => {
        setShowDetailsForEmployee(!showDetailsForEmployee);
    };
    const toggleDetailsForDisableEmployee = () => {
        setShowDetailsForDisableEmployee(!showDetailsForDisableEmployee);
    };

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
    const validateField = (name, value) => {
        let error = '';
        if (!value) {
            switch (name) {
                case 'firstName':
                    error = 'First name is required';
                    break;
                case 'lastName':
                    error = 'Last name is required';
                    break;
                case 'phoneNumber':
                    error = 'Phone number is required';
                    break;
                case 'email':
                    error = 'Email is required';
                    break;
                case 'resume':
                    error = 'Resume is required';
                    break;
                default:
                    break;
            }
        }
        setFormErrors((prev) => ({ ...prev, [name]: error }));
        return error === '';
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        const newValue = files ? files[0] : value;

        setFormData((prev) => ({ ...prev, [name]: newValue }));

        if (formErrors[name]) validateField(name, newValue); // Real-time error handling
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        validateField(name, value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.resume) {
            setFormErrors(prev => ({
                ...prev,
                resume: 'Resume is required'
            }));
            return;
        }

        // Validate all fields
        const isValid = Object.keys(formErrors).every((field) =>
            validateField(field, formData[field])
        );

        if (!isValid) return;

        setIsSubmitting(true);
        try {
            const formDataToSend = new FormData();

            // Append all form fields to FormData
            Object.keys(formData).forEach((key) => {
                if (key === 'resume' || key === 'coverLetter') {
                    if (formData[key]) {
                        formDataToSend.append(key, formData[key]);
                    }
                } else {
                    formDataToSend.append(key, formData[key]);
                }
            });

            const response = await fetch(`${API_BASE_URL}/api/job-application`, {
                method: 'POST',
                body: formDataToSend,
            });

            if (response.ok) {
                setDialogState({
                    open: true,
                    title: 'Success',
                    message: 'Your application has been submitted successfully.',
                    severity: 'success'
                });
                setFormData(getInitialData());

                // Clear file input fields
                const resumeInput = document.querySelector('input[name="resume"]');
                const coverLetterInput = document.querySelector('input[name="coverLetter"]');
                if (resumeInput) resumeInput.value = '';
                if (coverLetterInput) coverLetterInput.value = '';
            } else {
                setDialogState({
                    open: true,
                    title: 'Error',
                    message: 'There was an issue submitting your application. Please try again.',
                    severity: 'error'
                });
            }
        } catch (error) {
            setDialogState({
                open: true,
                title: 'Error',
                message: 'An error occurred. Please try again later.',
                severity: 'error'
            });
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <><div className='job-application-form-container'>
            <form className='job-form' onSubmit={handleSubmit}>
                <h1>Apply for this job</h1>
                <div className="job-form-row">
                    <div className='custom-text-field'>
                        <label className="custom-label">
                            Position
                        </label>
                        <CustomTextField
                            name="position"
                            value={formData.position}
                            disabled
                            fullWidth
                            className='custom-text-field'
                        />
                    </div>
                </div>
                <div className="job-form-row">

                    <div className='custom-text-field'>
                        <label className="custom-label" htmlFor="first-name">
                            First Name
                        </label>

                        <CustomTextField
                            name="firstName"
                            required
                            value={formData.firstName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!formErrors.firstName}
                            helperText={formErrors.firstName}
                            fullWidth
                            className='custom-text-field' />
                    </div>


                    <div className='custom-text-field'>
                        <label className="custom-label" htmlFor="last-name">
                            Last Name
                        </label>

                        <CustomTextField
                            name="lastName"
                            required
                            value={formData.lastName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!formErrors.lastName}
                            helperText={formErrors.lastName}
                            fullWidth
                            className='custom-text-field' />
                    </div>
                </div>

                <div className="job-form-row">

                    <div className='custom-text-field'>
                        <label className="custom-label" htmlFor="phone-number">
                            Phone Number
                        </label>

                        <CustomTextField
                            name="phoneNumber"
                            required
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!formErrors.phoneNumber}
                            helperText={formErrors.phoneNumber}
                            fullWidth
                            className='custom-text-field' />
                    </div>


                    <div className='custom-text-field'>
                        <label className="custom-label" htmlFor="email">
                            Email
                        </label>
                        <CustomTextField
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!formErrors.email}
                            helperText={formErrors.email}
                            fullWidth
                            className='custom-text-field' />
                    </div>
                </div>

                <div className="job-form-row">
                    <Box className="file-input">
                        <label>Resume/CV</label>
                        <div className='uploader-wrapper'>
                            <input
                                type="file"
                                name="resume"
                                onChange={handleChange}
                                accept=".pdf,.doc,.docx"
                            />
                        </div>
                        {formErrors.resume && (
                            <span className="error-text">{formErrors.resume}</span>
                        )}
                    </Box>
                    <Box className="file-input">
                        <label>Cover Letter</label>
                        <div className='uploader-wrapper'>
                            <input
                                type="file"
                                name="coverLetter"
                                onChange={handleChange}
                                accept=".pdf,.doc,.docx" />
                        </div>
                    </Box>
                </div>

                {jobSpecificFields.map((field, index) => (
                    <div className="specific-field" key={index}>
                        <div className='specific-text-field'>
                            <label className="custom-label" htmlFor={field.name}>
                                {field.label}
                            </label>
                            {field.type === 'select' ? (
                                <select
                                    name={field.name}
                                    value={formData[field.name]}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className='custom-text-field'
                                >
                                    {field.options.map((option, idx) => (
                                        <option key={idx} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    name={field.name}
                                    value={formData[field.name]}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className='custom-text-field'
                                />
                            )}
                        </div>
                    </div>
                ))}
                <div className='employment-information'>
                    <h2>U.S. Equal Opportunity Employment Information <i className='bx bx-info-circle' onClick={toggleDetailsForEmployee}></i></h2>
                    {showDetailsForEmployee && (
                        <div className='useDetails'>
                            <p>
                                Individuals seeking employment at Virginia Rehab Physicians are considered without regards to race, color, religion, national origin, age, sex, marital status, ancestry, physical or mental disability, veteran status, gender identity, or sexual orientation. You are being given the opportunity to provide the following information in order to help us comply with federal and state Equal Employment Opportunity/Affirmative Action record keeping, reporting, and other legal requirements.
                            </p>
                            <br />

                            <p>
                                Completion of the form is entirely voluntary. Whatever your decision, it will not be considered in the hiring process or thereafter. Any information that you do provide will be recorded and maintained in a confidential file.
                            </p>
                            <br />

                            <p>
                                If you believe you belong to any of the categories of protected veterans listed below, please indicate by making the appropriate selection. Classification of protected categories is as follows:
                            </p>
                            <br />

                            <p>
                                A "disabled veteran" is one of the following: a veteran of the U.S. military, ground, naval or air service who is entitled to compensation (or who but for the receipt of military retired pay would be entitled to compensation) under laws administered by the Secretary of Veterans Affairs; or a person who was discharged or released from active duty because of a service-connected disability.
                            </p>
                            <br />

                            <p>
                                A "recently separated veteran" means any veteran during the three-year period beginning on the date of such veteran's discharge or release from active duty in the U.S. military, ground, naval, or air service.
                            </p>
                            <br />

                            <p>
                                An "active duty wartime or campaign badge veteran" means a veteran who served on active duty in the U.S. military, ground, naval or air service during a war, or in a campaign or expedition for which a campaign badge has been authorized under the laws administered by the Department of Defense.
                            </p>
                            <br />

                            <p>
                                An "Armed forces service medal veteran" means a veteran who, while serving on active duty in the U.S. military, ground, naval or air service, participated in a United States military operation for which an Armed Forces service medal was awarded pursuant to Executive Order 12985.
                            </p>
                        </div>)}
                </div>
                <div className="job-form-row specific-field">
                    <div className='specific-text-field'>
                        <label className="custom-label" htmlFor="gender">
                            Gender
                        </label>
                        <select
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className='custom-text-field'
                        >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>
                    <div className='specific-text-field'>
                        <label className="custom-label" htmlFor="veteran-status">
                            Veteran Status
                        </label>
                        <select
                            name="veteranStatus"
                            value={formData.veteranStatus}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className='custom-text-field'
                        >
                            <option value="">Select Veteran Status</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                        </select>
                    </div>
                    <div className='specific-text-field'>
                        <label className="custom-label" htmlFor="hispanic">
                            Are You Hispanic/Latino?
                        </label>
                        <select
                            name="isHispanic"
                            value={formData.isHispanic}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className='custom-text-field'
                        >
                            <option value="">Select Option</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                        </select>
                    </div>


                </div>
                <div className='identification-of-disability'>
                    <h2>Voluntary Self-Identification of Disability <i className='bx bx-info-circle' onClick={toggleDetailsForDisableEmployee}></i></h2>
                    {showDetailsForDisableEmployee && (
                        <div className='useDetails'>
                            <p>
                                At Virginia Rehab Physicians, we are committed to creating an inclusive and diverse workplace where all individuals are valued. We recognize that people with disabilities face unique challenges and are dedicated to providing a supportive environment for all of our employees.
                            </p>
                            <br />

                            <p>
                                As part of our commitment to equal opportunity, we ask applicants and employees to voluntarily self-identify if they have a disability. This disclosure is completely voluntary and will not affect any employment decisions. Your decision to provide or not provide this information will be kept confidential and separate from your personnel records.
                            </p>
                            <br />

                            <p>
                                The information collected will be used solely for statistical purposes and to ensure compliance with federal, state, and local regulations regarding disability rights and affirmative action requirements. It will also help us to assess the effectiveness of our diversity and inclusion efforts, and to ensure that we are meeting our obligations to provide reasonable accommodations to employees who may need them.
                            </p>
                            <br />

                            <p>
                                <strong>What does "disability" mean?</strong>
                                <br />
                                <p>A disability, as defined under the Americans with Disabilities Act (ADA), is a physical or mental impairment that substantially limits one or more major life activities. These include, but are not limited to, walking, seeing, hearing, speaking, learning, and working. Disabilities may be visible or invisible and include chronic conditions or impairments that may impact daily activities.</p>
                            </p>
                            <br />

                            <p>
                                <strong>Confidentiality:</strong>
                                <br />
                                <p>All information you provide will be kept confidential and stored separately from your personnel file. It will only be used in compliance with legal and regulatory requirements and for the purpose of improving our workplace practices.</p>
                            </p>
                            <br />

                            <p>
                                If you have any questions about this disclosure or wish to discuss accommodations, please reach out to us for support.
                            </p>
                            <br />

                            {/* <p>
                                An "Armed forces service medal veteran" means a veteran who, while serving on active duty in the U.S. military, ground, naval or air service, participated in a United States military operation for which an Armed Forces service medal was awarded pursuant to Executive Order 12985.
                            </p> */}
                        </div>)}
                </div>
                <div className="job-form-row specific-field">
                    <div className='specific-text-field'>
                        <label className="custom-label" htmlFor="disability-status">
                            Disability Status
                        </label>
                        <select
                            name="disabilityStatus"
                            value={formData.disabilityStatus}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className='custom-text-field'
                        >
                            <option value="">Select Disability Status</option>
                            <option value="Yes, I have a disability, or have a history/record of having a disability">
                                Yes, I have a disability
                            </option>
                            <option value="No, I don't have a disability, or a history/record of having a disability">
                                No, I don't have a disability
                            </option>
                            <option value="I don't wish to answer">
                                I don't wish to answer
                            </option>
                        </select>
                    </div>

                </div>
                <div className='submit-btn-container'>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Submit Application'
                        )}
                    </Button>
                </div>
            </form>
        </div>
            <GenericDialog
                open={dialogState.open}
                onClose={() => setDialogState({ ...dialogState, open: false })}
                title={dialogState.title}
                message={dialogState.message}
                severity={dialogState.severity}
            />
        </>
    );
};

export default JobApplicationForm;
